import { useState } from 'react'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { Fab, IconButton } from '@mui/material'
import PageLoading from '../../../components/PageLoading/PageLoading'
import TableHeader from '../../../components/TableHeader/TableHeader'
import InformationTable from '../../../components/Table/InformationTable'
import { useNotification } from '../../../context/NotificationManager/NotificationManager'
import {
  createCuadernillo,
  deleteCuadernillo,
  getCuadernilloList,
  updateCuadernillo
} from '../../../utils/api/materialDocente'
import { textFilter } from '../../../utils/table'
import ModalEditarCuadernillo from '../../../modals/ModalEditarCuadernillo/ModalEditarCuadernillo'
import ModalConfirmacion from '../../../modals/ModalConfirmacion/ModalConfirmacion'
import { isRolBrain } from '../../../utils/auth'
import ModalNuevoCuadernillo from '../../../modals/ModalNuevoCuadernillo/ModalNuevoCuadernillo'
import ModalAsignaturas from '../../../modals/ModalAsignaturas/ModalAsignaturas'
import ModalPdfViewer from '../../../modals/ModalPdfViewer/ModalPdfViewer'
import AsignaturaSelector from '../../../components/form/AsignaturaSelector/AsignaturaSelector'
import { pluralize } from '../../../utils/string'
import css from './Cuadernillos.module.css'

const Cuadernillos = () => {
  const queryClient = useQueryClient()
  const notification = useNotification()
  const [search, setSearch] = useState('')
  const [cuadernilloSeleccionado, setCuadernilloSeleccionado] = useState(null)
  const [isOpenEditarCuadernillo, setIsOpenEditarCuadernillo] = useState(false)
  const [isOpenEliminarCuadernillo, setIsOpenEliminarCuadernillo] =
    useState(false)
  const [isModalOpen, setIsModalOpen] = useState(false)
  const [isOpenAsignaturasModal, setIsOpenAsignaturasModal] = useState(false)
  const [isOpenPdfModal, setIsOpenPdfModal] = useState(false)
  const [selectedAsignatura, setSelectedAsignatura] = useState(null)

  const accesoBrain = isRolBrain()

  const { isLoading, data: cuadernilloList = [] } = useQuery({
    queryKey: ['material', 'cuadernillo'],
    queryFn: () =>
      getCuadernilloList().catch(err => {
        notification.error({
          title: 'Error al recuperar los cuadernillos',
          content: err
        })
        return []
      })
  })

  const { isPending: isCuadernilloCreating, mutate: crearCuadernillo } =
    useMutation({
      mutationFn: createCuadernillo,
      onSuccess: () => {
        notification.success({
          title: 'Cuadernillo enviado',
          content: 'Se ha enviado el cuadernillo'
        })
        queryClient.invalidateQueries({
          queryKey: ['material', 'cuadernillo']
        })
        setIsModalOpen(false)
      },
      onError: err => {
        notification.error({
          title: 'Error al enviar el cuadernillo',
          content: err
        })
      }
    })

  const { isPending: isCuadernilloUpdating, mutate: actualizarCuadernillo } =
    useMutation({
      mutationFn: updateCuadernillo,
      onSuccess: () => {
        queryClient.invalidateQueries(['material', 'cuadernillo'])
        notification.success({
          title: 'Cuadernillo actualizado',
          content: 'Datos modificados correctamente'
        })
        setIsOpenEditarCuadernillo(false)
      },
      onError: err => {
        notification.error({
          title: 'Error actualizando el cuadernillo',
          content: err
        })
      }
    })

  const { isPending: isCuadernilloDeleting, mutate: borrarCuadernillo } =
    useMutation({
      mutationFn: deleteCuadernillo,
      onSuccess: () => {
        queryClient.invalidateQueries(['material', 'cuadernillo'])
        notification.success({
          title: 'Cuadernillo borrado',
          content: 'Se ha borrado el cuadernillo correctamente'
        })
        setCuadernilloSeleccionado(null)
        setIsOpenEliminarCuadernillo(false)
        closeModal()
      },
      onError: err => {
        notification.error({
          title: 'Error al borrar el cuadernillo',
          content: err
        })
      }
    })

  const handleNuevoCuadernillo = ({ nombre, asignaturas, archivo }) => {
    if (isCuadernilloCreating) return
    crearCuadernillo({ nombre, asignaturas, archivo })
  }

  const handleUpdateCuadernillo = updatedCuadernillo => {
    if (isCuadernilloUpdating) return
    actualizarCuadernillo(updatedCuadernillo)
  }

  const handleDeleteCuadernillo = () => {
    if (isCuadernilloDeleting) return
    borrarCuadernillo(cuadernilloSeleccionado.id)
  }

  const filterSearch = cuadernillo => {
    const matchesSearch = textFilter({
      object: cuadernillo,
      fields: ['nombre'],
      search: search
    })

    const matchesAsignatura =
      !selectedAsignatura ||
      cuadernillo.asignaturas.some(
        asignatura => asignatura.id === selectedAsignatura.id
      )

    return matchesSearch && matchesAsignatura
  }

  const openModal = cuadernillo => {
    setCuadernilloSeleccionado(cuadernillo)
    setIsOpenEditarCuadernillo(true)
  }

  const closeModal = () => {
    setIsOpenEditarCuadernillo(false)
    setCuadernilloSeleccionado(null)
  }

  return (
    <PageLoading isLoading={isLoading}>
      <TableHeader
        title='Cuadernillos'
        search={search}
        onSearchChange={e => setSearch(e.target.value)}
        actions={
          <AsignaturaSelector
            label='Asignatura'
            value={selectedAsignatura?.id || null}
            onChange={(_event, newValue) => setSelectedAsignatura(newValue)}
            className={css.asignaturaFilter}
          />
        }
      />
      <InformationTable
        details={[
          { title: 'Nombre', key: 'nombre', sortKey: 'nombreCuadernillo' },
          {
            title: 'Asignaturas',
            key: 'asignaturas',
            sortKey: 'totalAsignaturas'
          },
          { title: 'Opciones', key: 'opciones', align: 'center' }
        ]}
        data={cuadernilloList.filter(filterSearch).map(cuadernillo => ({
          nombre: (
            <span
              className={css.link}
              onClick={() => {
                setCuadernilloSeleccionado(cuadernillo)
                setIsOpenPdfModal(true)
              }}
            >
              {cuadernillo.nombre || '--'}
            </span>
          ),
          nombreCuadernillo: cuadernillo.nombre,
          totalAsignaturas: cuadernillo.asignaturas.length,
          asignaturas:
            cuadernillo.asignaturas.length > 0 ? (
              <span
                className={css.asignaturasCount}
                onClick={() => {
                  setCuadernilloSeleccionado(cuadernillo)
                  setIsOpenAsignaturasModal(true)
                }}
              >
                {cuadernillo.asignaturas.length}{' '}
                {pluralize('asignatura', cuadernillo.asignaturas.length)}
              </span>
            ) : (
              <span className={css.sinAsignaturas}>Sin asignaturas</span>
            ),
          opciones: (
            <>
              <IconButton
                size='small'
                color='secondary'
                disabled={!accesoBrain}
                onClick={() => openModal(cuadernillo)}
              >
                <i className='material-icons'>create</i>
              </IconButton>
              <IconButton
                size='small'
                color='error'
                disabled={!accesoBrain}
                onClick={() => {
                  setCuadernilloSeleccionado(cuadernillo)
                  setIsOpenEliminarCuadernillo(true)
                }}
              >
                <i className='material-icons'>delete</i>
              </IconButton>
            </>
          )
        }))}
        isLoading={isLoading}
        isFetching={isLoading}
      />
      <ModalNuevoCuadernillo
        open={isModalOpen}
        onSubmit={handleNuevoCuadernillo}
        onClose={() => setIsModalOpen(false)}
        isLoading={isCuadernilloCreating}
      />
      <ModalEditarCuadernillo
        open={isOpenEditarCuadernillo}
        cuadernillo={cuadernilloSeleccionado}
        onClose={closeModal}
        isLoading={isCuadernilloUpdating}
        onSubmit={handleUpdateCuadernillo}
      />
      <ModalConfirmacion
        title='¿Seguro que quieres borrar el cuadernillo?'
        content='Al borrar el cuadernillo, dejará de estar disponible en el área de profesores, así como para la selección de material que se realiza en Classfy.'
        open={isOpenEliminarCuadernillo}
        onClose={() => setIsOpenEliminarCuadernillo(false)}
        onClick={handleDeleteCuadernillo}
      />
      <ModalAsignaturas
        open={isOpenAsignaturasModal}
        onClose={() => setIsOpenAsignaturasModal(false)}
        name={cuadernilloSeleccionado?.nombre || ''}
        asignaturas={cuadernilloSeleccionado?.asignaturas || []}
      />
      <ModalPdfViewer
        open={isOpenPdfModal}
        onClose={() => setIsOpenPdfModal(false)}
        title={cuadernilloSeleccionado?.nombre || ''}
        pdfUrl={cuadernilloSeleccionado?.url || ''}
      />
      {accesoBrain && (
        <Fab
          className={css.nuevo}
          onClick={() => setIsModalOpen(true)}
          size='medium'
          color='primary'
          aria-label='add'
        >
          <i className='material-icons'>add</i>
        </Fab>
      )}
    </PageLoading>
  )
}

export default Cuadernillos
